/**
 * The countries Xeito can express a location in: the 27 EU member states, the
 * 4 EFTA states (Iceland, Liechtenstein, Norway, Switzerland — note the UK is
 * NOT in EFTA; it left in 1973 for the EEC and didn't rejoin post-Brexit), the
 * UK itself, and — since B-new-605 — the United States.
 *
 * Canonical source of truth for ISO-2 codes used across onboarding
 * (users.locations), job-search filtering (job_posts.remote_country_restriction),
 * and the Settings-page location picker. The browser extension
 * (apps/job-tracker-extension/lib/eu-countries.js) is unbundled JS and can't
 * import from packages/ — its country list is hand-synced from this one, not
 * a bug.
 *
 * ⚠ TWO SETS, DELIBERATELY. `COUNTRIES` is the vocabulary — what a country can
 * be NAMED as, used to classify what a job listing says. `EU_EFTA_UK_COUNTRIES`
 * is the subset a USER is currently allowed to store as their own location.
 *
 * They are not the same question and must not be merged until B-new-609. One
 * list governed both until 2026-09-05, which is why adding the US to it in a
 * single step would have silently widened the user-location allowlist at the
 * three Zod write paths (`onboarding.ts:22`, `:329`, `user.ts:334`) — and a
 * US-located user against today's catalogue sees 5,845 EU-only listings, every
 * one of them undoable. The subset is DERIVED from the vocabulary below rather
 * than hand-maintained, so the two can never drift.
 */

export type CountryBloc = 'EU' | 'EFTA' | 'UK' | 'US';

export interface Country {
  code: string;
  name: string;
  flag: string;
  bloc: CountryBloc;
  /**
   * Additional lowercase surface forms a listing might use for this country.
   * `name` alone is not enough: a posting says "Remote - USA" far more often
   * than "Remote - United States". Consumed by the restriction detector.
   */
  aliases?: readonly string[];
}

/** @deprecated Use `Country`. Kept so the rename is not atomic across callers. */
export type EuEftaUkCountry = Country;

export const COUNTRIES: readonly Country[] = [
  // 27 EU member states
  { code: 'AT', name: 'Austria', flag: '🇦🇹', bloc: 'EU' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', bloc: 'EU' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', bloc: 'EU' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷', bloc: 'EU' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾', bloc: 'EU' },
  { code: 'CZ', name: 'Czechia', flag: '🇨🇿', bloc: 'EU' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', bloc: 'EU' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪', bloc: 'EU' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', bloc: 'EU' },
  { code: 'FR', name: 'France', flag: '🇫🇷', bloc: 'EU' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', bloc: 'EU' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', bloc: 'EU' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', bloc: 'EU' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', bloc: 'EU' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', bloc: 'EU' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻', bloc: 'EU' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹', bloc: 'EU' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', bloc: 'EU' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹', bloc: 'EU' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', bloc: 'EU' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', bloc: 'EU' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', bloc: 'EU' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', bloc: 'EU' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰', bloc: 'EU' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮', bloc: 'EU' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', bloc: 'EU' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', bloc: 'EU' },
  // 4 EFTA states
  { code: 'IS', name: 'Iceland', flag: '🇮🇸', bloc: 'EFTA' },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', bloc: 'EFTA' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', bloc: 'EFTA' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', bloc: 'EFTA' },
  // UK
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', bloc: 'UK' },
  // Non-European supported locations — B-new-605.
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    bloc: 'US',
    aliases: ['usa', 'u.s.', 'u.s.a.', 'united states of america', 'america'],
  },
];

/** Blocs that make up the European subset. */
const EUROPEAN_BLOCS: ReadonlySet<CountryBloc> = new Set<CountryBloc>(['EU', 'EFTA', 'UK']);

/**
 * The 32 EU/EFTA/UK countries — the subset a USER may currently store as their
 * own location, and the meaning of "workable anywhere in Europe" when deriving
 * a listing's workable-country set.
 *
 * Derived, never hand-listed: a new entry in COUNTRIES joins this set only if
 * its bloc is European, so the two cannot drift apart.
 */
export const EU_EFTA_UK_COUNTRIES: readonly Country[] = COUNTRIES.filter((c) =>
  EUROPEAN_BLOCS.has(c.bloc),
);

// ── Full vocabulary (what a listing may NAME) ────────────────────────────────

export const COUNTRY_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((c) => c.code));

export const COUNTRY_BY_CODE: ReadonlyMap<string, Country> = new Map(
  COUNTRIES.map((c) => [c.code, c]),
);

/**
 * Every lowercase surface form → ISO-2, including aliases. "usa" and "united
 * states" both resolve to US; `EU_EFTA_UK_NAME_TO_CODE` below deliberately
 * does not contain them.
 */
export const NAME_TO_CODE: ReadonlyMap<string, string> = new Map(
  COUNTRIES.flatMap((c) => [
    [c.name.toLowerCase(), c.code] as [string, string],
    ...(c.aliases ?? []).map((a) => [a, c.code] as [string, string]),
  ]),
);

export function isValidCountryCode(code: string | null | undefined): boolean {
  return typeof code === 'string' && COUNTRY_CODES.has(code);
}

// ── European subset (what a USER may store, until B-new-609) ─────────────────

export const EU_EFTA_UK_COUNTRY_CODES: ReadonlySet<string> = new Set(
  EU_EFTA_UK_COUNTRIES.map((c) => c.code),
);

export const EU_EFTA_UK_COUNTRY_BY_CODE: ReadonlyMap<string, Country> = new Map(
  EU_EFTA_UK_COUNTRIES.map((c) => [c.code, c]),
);

export const EU_EFTA_UK_NAME_TO_CODE: ReadonlyMap<string, string> = new Map(
  EU_EFTA_UK_COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]),
);

/**
 * Is this a country a USER may store as their own location today?
 *
 * Deliberately NOT widened to `isValidCountryCode` in B-new-605 S1. Widening it
 * is B-new-609, and it must land in the same change that deletes or per-users
 * `us-only-suppression.ts` — otherwise the first US-located user gets a
 * catalogue with precisely the US inventory stripped out, broken by the very
 * stopgap that made the EU promise true.
 */
export function isValidEuEftaUkCode(code: string | null | undefined): boolean {
  return typeof code === 'string' && EU_EFTA_UK_COUNTRY_CODES.has(code);
}

/** Display name for any code in the full vocabulary, US included. */
export function getCountryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return COUNTRY_BY_CODE.get(code)?.name ?? null;
}
